import { z } from "zod";
import { CCS_CARE_TYPES } from "../au13/index.js";
import type { FinancialYear } from "../types.js";

// ---------------------------------------------------------------------------
// second-earner — "what does an extra day of work actually add?"
//
// For a second earner in a couple family with children in subsidised care, the
// take-home value of an extra DAY of work is not the gross day-rate: that day
// of income is
//   (a) taxed at the second earner's OWN marginal rate (income-tax engine),
//   (b) lifts COMBINED family income, which withdraws Family Tax Benefit Part A
//       under the two-stage taper (20c then 30c), and
//   (c) lifts combined family income again, stepping DOWN the family's CCS
//       applicable percentage (au13 CCS engine) so the SAME childcare costs more
//       out of pocket.
//
// The tool walks days 1 → `daysToModel` (capped at 5), attributing each day's
// gross to its own slice of each taper, and reports the PER-DAY NET GAIN beside
// the PER-DAY GROSS. This mirrors Treasury's "workforce disincentive rate"
// framing (Round Up, Mar 2024): WDRs exceed 60% beyond one day and over 80% of
// the fifth day's earnings can be lost — but the tool reports the user's own
// per-day mechanics, NOT that aggregate figure.
//
// Like income-tax / au13 this is NOT a per-product (au0x) ruleset — it has no
// `product` code. Its rulesets carry an `engine: "second-earner"` tag plus the
// usual provenance fields. Calculation is pure: inputs -> rules -> outputs, the
// assessment FY is always passed in, no Date.now(), no I/O.
//
// REGULATORY POSTURE (binding — validation-plan §3, panel ship-conditions):
// information, not advice. HARD RULE: this surface shows GROSS beside NET and
// presents the per-day waterfall as NEUTRAL MECHANICS. It NEVER outputs a
// "worth it / not worth it / stay home / work more" verdict — that is both
// advice-drift AND misleading (it omits gross, super, career progression). The
// absence of any verdict field is enforced by a guard + a test. The CCS step is
// reused wholesale from the shipped au13 ruleset; the marginal-tax step from the
// income-tax engine. Affiliate payout is ~$0 (ledger Fig 5) and is NOT baked in;
// no financial-product handoff attaches to a result (hold the C2 line).
// ---------------------------------------------------------------------------

/**
 * Family Tax Benefit Part A income test (Services Australia / DSS FA Guide).
 * Two-stage taper on COMBINED adjusted taxable income:
 *   - the MAX-rate portion of FTB-A reduces by `lowerTaperRate` (20c) per $1
 *     above `lowerFreeArea`, until the family drops to its base rate;
 *   - the BASE-rate portion then reduces by `higherTaperRate` (30c) per $1 above
 *     `higherFreeArea`, until it reaches $0.
 *
 * We model the WITHDRAWAL marginally (how much FTB-A this day of income strips),
 * capped so a family's FTB-A can never be withdrawn below $0. The family's own
 * max-rate and base-rate annual entitlements are CALLER-SUPPLIED (a figure the
 * user knows from their Services Australia statement) — we do NOT invent
 * per-child FTB-A rates, only the verified thresholds + taper rates live here.
 */
export interface FtbATaperRules {
  /** Lower income free area — max-rate FTB-A starts reducing above this (2025-26
   * $66,722; ledger Fig 3, AGENT_VERIFIED). */
  lowerFreeArea: number;
  /** Max-rate taper: cents per $1 over `lowerFreeArea` (0.20). */
  lowerTaperRate: number;
  /** Higher income free area — base-rate FTB-A starts reducing above this
   * (2025-26 $118,771; ledger Fig 3). */
  higherFreeArea: number;
  /** Base-rate taper: cents per $1 over `higherFreeArea` (0.30). */
  higherTaperRate: number;
}

/** A complete FY second-earner ruleset, keyed by financial year. */
export interface SecondEarnerRuleSet {
  /** Engine tag — distinguishes shared engines from per-product rulesets. */
  engine: "second-earner";
  /** Financial-year key, e.g. "2025-26". */
  fy: FinancialYear;
  /** Inclusive FY start (1 July). */
  effectiveFrom: string;
  /** Inclusive FY end (30 June). */
  effectiveTo: string;
  /** Source-of-truth URLs the figures were transcribed from. */
  sources: Record<string, string>;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;
  /** The FTB-A two-stage income test (thresholds + taper rates). */
  ftbA: FtbATaperRules;
  /** Fortnights per year — annualises the au13 fortnightly CCS gap (26). */
  fortnightsPerYear: number;
}

// ---------------------------------------------------------------------------
// Calculation inputs / outputs
// ---------------------------------------------------------------------------

/**
 * The family's CURRENT FTB Part A annual entitlement, split by the two rate
 * components so the two-stage taper can withdraw each in turn. Both are what the
 * family receives BEFORE the second earner's extra days lift combined income.
 * Caller-supplied (from the Services Australia statement); 0/0 = no FTB-A.
 */
export const FtbAEntitlementSchema = z
  .object({
    /** Annual MAX-rate FTB-A component (the part the first 20c taper reduces). */
    maxRateAnnual: z.number().min(0).default(0),
    /** Annual BASE-rate FTB-A component (the part the second 30c taper reduces).
     * For most families this is the floor the max rate tapers DOWN to; supply it
     * as the base-rate entitlement the family keeps after the first taper. */
    baseRateAnnual: z.number().min(0).default(0),
  })
  .default({ maxRateAnnual: 0, baseRateAnnual: 0 });
export type FtbAEntitlement = z.infer<typeof FtbAEntitlementSchema>;

/**
 * One child's subsidised-care setup, passed straight through to the shipped
 * au13 CCS engine. We recompute this child's CCS at the raised combined family
 * income to find the extra out-of-pocket gap each day creates. `familyIncome`
 * is NOT part of this — it is derived per-day from primary + second-earner
 * income inside the calc.
 */
export const SecondEarnerChildSchema = z.object({
  /** Hours of subsidised care used per fortnight for this child. */
  hoursUsed: z.number().min(0),
  /** Care type (selects the hourly rate cap). */
  careType: z.enum(CCS_CARE_TYPES),
  /** Actual hourly fee the service charges, in dollars. */
  hourlyFee: z.number().min(0),
  /** Recognised activity hours per fortnight (lower of the two partners). */
  activityHours: z.number().min(0).default(0),
  /** True if the child attends school (selects the school-aged rate cap). */
  schoolAged: z.boolean().default(false),
  /** Aboriginal or Torres Strait Islander child. */
  indigenousChild: z.boolean().default(false),
  /** 1 = eldest/only; 2 = second-or-younger child (higher CCS rate). */
  childRank: z.union([z.literal(1), z.literal(2)]).default(1),
});
export type SecondEarnerChild = z.infer<typeof SecondEarnerChildSchema>;

export const SecondEarnerInputSchema = z.object({
  /** The PRIMARY earner's annual taxable income — fixed across the scenario.
   * Combined with the second earner's cumulative day income it sets family
   * income for the FTB-A and CCS tests. */
  primaryIncome: z.number().min(0),
  /** The second earner's annual taxable income BEFORE adding the modelled days
   * (e.g. they already work some days). The extra days stack on top of this. */
  secondEarnerBaseIncome: z.number().min(0).default(0),
  /** Gross pay for ONE additional day of the second earner's work, in dollars.
   * Each modelled day adds this to the second earner's income. */
  dayGross: z.number().min(0),
  /** How many extra days to model, 1–5. The per-day curve runs day 1 → this. */
  daysToModel: z.number().int().min(1).max(5).default(5),
  /** The family's current FTB Part A annual entitlement (max + base split). */
  ftbAEntitlement: FtbAEntitlementSchema,
  /** The children in subsidised care whose out-of-pocket gap the extra days
   * affect (CCS recomputed at the raised combined income). Empty = no care. */
  children: z.array(SecondEarnerChildSchema).default([]),
  /** Whether the second earner has an outstanding HELP/STSL loan (so an extra
   * day can also lift their compulsory repayment). Defaults to false. */
  hasStudyLoan: z.boolean().default(false),
  /** The financial year being assessed, e.g. "2025-26". Passed in (never from
   * the clock) so historical re-runs resolve against the right ruleset. */
  assessmentYear: z.string(),
});
export type SecondEarnerInput = z.input<typeof SecondEarnerInputSchema>;
export type SecondEarnerParsed = z.infer<typeof SecondEarnerInputSchema>;

/**
 * The decomposition of ONE modelled day. Every component is the MARGINAL amount
 * that day of gross strips — income tax + levy, FTB-A withdrawal, extra CCS
 * out-of-pocket — plus the resulting net. `keepRate` = net ÷ gross is reported
 * as a neutral mechanic (the complement of Treasury's WDR), NEVER as a verdict.
 */
export interface SecondEarnerDay {
  /** Day index, 1-based (1 = the first extra day). */
  day: number;
  /** GROSS pay for this day (always shown beside net — binding copy rule). */
  gross: number;
  /** Extra income tax + Medicare levy this day's income attracts (marginal). */
  extraTax: number;
  /** Extra compulsory HELP/STSL repayment this day causes (0 if no loan). */
  extraHelp: number;
  /** FTB Part A withdrawn by this day of combined-income rise (marginal). */
  ftbWithdrawn: number;
  /** Extra out-of-pocket childcare this day causes via the CCS step-down
   * (annualised across all children, marginal). */
  extraChildcare: number;
  /** NET gain for this day = gross − extraTax − extraHelp − ftbWithdrawn −
   * extraChildcare. May be small or near-zero on later days; never below 0 in
   * the modelled mechanics (clamped). */
  net: number;
  /** net ÷ gross, a fraction in [0, 1]. The keep-rate — a NEUTRAL mechanic, the
   * complement of the workforce-disincentive rate. null when gross is 0. */
  keepRate: number | null;
}

export interface SecondEarnerOutput {
  /** The primary earner's income the scenario ran against. */
  primaryIncome: number;
  /** The second earner's base income before the modelled days. */
  secondEarnerBaseIncome: number;
  /** Gross per modelled day. */
  dayGross: number;
  /** The per-day decomposition, day 1 → `daysToModel`. */
  days: SecondEarnerDay[];
  /** Total GROSS across all modelled days (shown beside total net). */
  totalGross: number;
  /** Total NET across all modelled days. */
  totalNet: number;
}
